import type { UseMutationResult } from '@tanstack/react-query'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSWRConfig } from 'swr'
import {
  COMMENT_API_PREFIX,
  COMMUNITY_SECURE_GLOBAL_PREFIX,
} from 'shared/api/constants/api-constants'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import type { ReportRequest } from '../types/moderation'
import { communityKeys, communityReportKeys } from './key-factory'

export const useReportComment = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void
  onError?: (error: Error) => void
}): UseMutationResult<
  void,
  Error,
  {
    body: ReportRequest
    communityPath: string
    postId: number
    commentId: number
  }
> => {
  const queryClient = useQueryClient()
  const { mutate: mutateSWR } = useSWRConfig()

  const { fetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.post,
    void,
    ReportRequest
  >({
    method: RequestMethodsEnum.post,
    badRequestHandler: () => {},
    notFoundResponseHandler: () => {},
    accessDeniedResponseHandler: () => {},
    unprocessableContentHandler: () => {},
    conflictResponseHandler: () => {},
  })

  return useMutation({
    mutationFn: ({ body, communityPath, postId, commentId }) =>
      fetcher(
        `${COMMUNITY_SECURE_GLOBAL_PREFIX}/${communityPath}/report/comment/${commentId}?postId=${postId}`,
        body,
      ),

    onSuccess: onSuccess ? () => onSuccess() : undefined,

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: communityKeys.commentGeneral() })
      void queryClient.invalidateQueries({ queryKey: communityReportKeys.all })

      void mutateSWR(
        (key: unknown) => typeof key === 'string' && key.startsWith(COMMENT_API_PREFIX),
        undefined,
        { revalidate: true },
      )
    },

    onError: onError ? error => onError(error) : undefined,
  })
}
